

import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import NotificationRuleActionDetails from "./NotificationRuleActionDetails.vue";
import NotificationRuleAction, { DefaultNotificationRuleAction } from "@/types/sv-data/notification-rule/NotificationRuleAction";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import { v4 as uuidv4 } from "uuid";
import { Getter } from "vuex-class";
import { UserPermissions } from "@/store/types";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import api from "@/services/api.service";
import ShortContact from "@/types/sv-data/users/ShortContact";
import NotificationRuleActionContactMembership from "@/types/sv-data/notification-rule/NotificationRuleActionContactMembership";

@Component({
    components: {
        "notification-rule-action-details": NotificationRuleActionDetails
    },
})
export default class NotificationRuleActions extends Vue {

    @Prop({ type: Boolean, default: false })
    private readOnly: boolean;
        
    @Prop({type: Array, required: true})
    private actions: NotificationRuleAction[];

    @Prop({ type: Boolean, default: false})
    private isEdit: boolean;

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getUserTenantGroupId: number;

    private actionsValidStatus: boolean[] = [];
    private notificationTemplates: NotificationTemplate[] = [];
    private contacts: ShortContact[] = [];
    
    private async mounted(): Promise<void> {
        await this.loadNotificationTemplates();
        await this.getContacts();
        this.setActionStatus();
    }

    private get allowDelete(): boolean {
        return this.actions.length > 1;
    }
    
	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

    private get userIds(): number[] {
        return this.actions.flatMap((action: NotificationRuleAction) => 
                action.sendTo
                    .filter((membership: NotificationRuleActionContactMembership) => !!membership && !!membership.userId)
                    .map((membership: NotificationRuleActionContactMembership) => membership.userId!)
            );
    }

    @Watch("actions")
    private onActionsChanged(newValue: NotificationRuleAction[] | null, oldValue: NotificationRuleAction[] | null): void {
        if (!newValue || 
            !oldValue || 
            newValue.length !== oldValue.length || 
            newValue[0]?.notificationRuleId !== oldValue[0]?.notificationRuleId
        ) {
            this.setActionStatus();
        }
        this.getContacts();
    }
    
    private setActionStatus(): void {
        this.actionsValidStatus = [];
        this.actions?.forEach(() => this.actionsValidStatus.push(this.isEdit));
        this.triggerValidationUpdate();
    }

    private async getContacts(): Promise<void> {
        try {
            this.contacts = await api.getContactsPaginated({
                params: {
                    page: 1,
                    pageSize: 100,
                    searchTerm: "",
                    requiredUserIds: this.userIds
                },
                cancelToken: null
            });
        } catch (error) {
            console.error("Error when getting contacts", error)
        }
    }
    
    private addNewAction(): void {
        // Create a copy of the original DefaultNotificationRuleAction and ensure a new key is set
        this.actions.push({...DefaultNotificationRuleAction, key: uuidv4()});
        this.actionsValidStatus.push(false);
        this.triggerUpdateActions();
        this.triggerValidationUpdate();
    }

    private updateAction(index: number, action: NotificationRuleAction): void {
        Vue.set(this.actions, index, action);
        this.triggerUpdateActions();
        this.triggerValidationUpdate();
    }

    private deleteAction(index: number): void {
        this.actions.splice(index, 1);
        this.actionsValidStatus.splice(index, 1)
        this.triggerUpdateActions();
        this.triggerValidationUpdate();
    }

    private setIsValid(index: number, isValid: boolean): void {
        this.actionsValidStatus[index] = isValid;
        this.triggerValidationUpdate();
    }

    @Emit("updateActions")
    private async triggerUpdateActions(): Promise<NotificationRuleAction[]> {
        await this.getContacts();
        return this.actions;
    }

    @Emit("updateValidation")
    private triggerValidationUpdate(): boolean {
        return this.actionsValidStatus.every(status => status === true);
    }
    
	private async loadNotificationTemplates(): Promise<void> {
		if (!this.isNotificationTemplatesEnabled || !this.hasNotificationTemplatePermissions) {
			return;
		}

		try {
			this.notificationTemplates = await notificationTemplatesApi.getAllNotificationTemplates(NotificationTemplateTypeId.Event, this.getUserTenantGroupId);
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: " + ex);
		}
	}
}
